/**
 * Pure helpers for the FeedbackTriagePanel form. The component drives the
 * mutation and the render; this module owns the two computations whose
 * empty-string-versus-null behaviour is easy to get wrong:
 *
 *  - whether the form has unsaved changes (``isDirty``)
 *  - what payload to send to ``PUT /v1/admin/feedback/<id>`` (``buildSavePayload``)
 *
 * Co-located so a regression in either shows up under unit-test load rather
 * than only being caught by a manual triage round-trip.
 */

export interface TriageFormFields {
  /** Persisted ``admin_label`` from the server, or ``null`` if never set. */
  persistedLabel: string | null;
  /** Persisted ``admin_note`` from the server, or ``null`` if never set. */
  persistedNote: string | null;
  /** Current contents of the label input. */
  draftLabel: string;
  /** Current contents of the note textarea. */
  draftNote: string;
}

/**
 * ``true`` when either field's draft differs from what is persisted.
 *
 * Treats persisted ``null`` and an empty draft as equivalent: clearing a
 * never-set field is not a change. The label is compared as the save would
 * send it (see {@link normalizeLabel}).
 */
export function isDirty(fields: TriageFormFields): boolean {
  const labelChanged =
    normalizeLabel(fields.draftLabel) !== (fields.persistedLabel ?? null);
  const noteChanged = fields.draftNote !== (fields.persistedNote ?? "");
  return labelChanged || noteChanged;
}

export interface AdminUpdateRequestBody {
  admin_label: string | null;
  admin_note: string | null;
}

/**
 * Build the ``request`` body for the admin update mutation.
 *
 * Backend contract: an absent key means "leave the column alone", ``null``
 * means "clear the column to NULL", a string means "overwrite the column".
 * The form has no "leave alone" affordance — it always rewrites both label
 * and note. An empty field becomes ``null`` (a NULL column rather than an
 * empty string), and the label is trimmed (see {@link normalizeLabel}).
 */
export function buildSavePayload(fields: {
  draftLabel: string;
  draftNote: string;
}): AdminUpdateRequestBody {
  return {
    admin_label: normalizeLabel(fields.draftLabel),
    admin_note: fields.draftNote.length > 0 ? fields.draftNote : null,
  };
}

/**
 * The label as stored: trimmed, case kept, and ``null`` when blank. The
 * table filters and counts labels by exact value, so "dup" and "dup " would
 * otherwise be two labels. The server applies the same rule.
 */
function normalizeLabel(draftLabel: string): string | null {
  const trimmed = draftLabel.trim();
  return trimmed.length > 0 ? trimmed : null;
}
